import { safeApiRequest } from './apiClient';

const SERVICE_AREAS_ENDPOINT = '/api/service-areas';

/**
 * Teknik servisin yetki alanlarını getirir
 * Endpoint: GET /api/service-areas/tech-service/:teknikServisId
 * Backend route parametresi: teknikServisId (backend validation'ı params.teknikServisId kontrolü yapıyor)
 */
export const fetchServiceAreasByTechService = async (techServiceId) => {
  if (!techServiceId) {
    throw new Error('Teknik servis ID gereklidir');
  }
  
  // MongoDB ObjectId format kontrolü
  const id = String(techServiceId).trim();
  if (!/^[0-9a-fA-F]{24}$/.test(id)) {
    throw new Error('Geçersiz teknik servis ID formatı (MongoDB ObjectId olmalıdır)');
  }
  
  // Backend route'u muhtemelen /api/service-areas/tech-service/:teknikServisId olarak tanımlanmış
  // Express.js'te URL'de parametre adı önemli değil, sadece değer gönderiyoruz
  // Ama backend validation'ı params.teknikServisId kontrolü yapıyor
  // Bu yüzden endpoint'i aynı şekilde kullanıyoruz
  // Eğer hala çalışmazsa, backend route tanımını kontrol etmek gerekir
  return safeApiRequest(`${SERVICE_AREAS_ENDPOINT}/tech-service/${id}`, {
    method: 'GET',
    skipCsrf: true,
  });
};

/**
 * İlçenin yetkili servisi var mı kontrol eder
 */
export const checkDistrictServiceArea = async (districtId) => {
  if (!districtId) {
    throw new Error('İlçe ID gereklidir');
  }
  return safeApiRequest(`${SERVICE_AREAS_ENDPOINT}/check/${districtId}`, {
    method: 'GET',
    skipCsrf: true,
  });
};

/**
 * Birden fazla ilçenin yetkili servis durumunu toplu olarak kontrol eder
 * Endpoint: POST /api/service-areas/check-batch
 * @param {number[]|string[]} districtIds
 * @param {{ yetkiliKategoriIds?: string[], excludeTechServiceId?: string }} [options] — Seçili yaprak kategoriler ve hariç tutulacak teknik servis (düzenlenen kayıt)
 */
export const checkDistrictsBatch = async (districtIds, options = {}) => {
  if (!Array.isArray(districtIds) || districtIds.length === 0) {
    throw new Error('İlçe ID listesi gereklidir');
  }
  
  // Tüm ID'leri number'a çevir
  const formattedIds = districtIds.map(id => Number(id)).filter(id => id >= 101 && id <= 8199);
  
  if (formattedIds.length === 0) {
    throw new Error('Geçerli ilçe ID\'si bulunamadı (101-8199 arası olmalıdır)');
  }

  const body = { districtIds: formattedIds };
  const { yetkiliKategoriIds, yetkiliUrunIcerikIds, excludeTechServiceId } = options || {};
  if (Array.isArray(yetkiliKategoriIds) && yetkiliKategoriIds.length > 0) {
    const valid = yetkiliKategoriIds
      .map((k) => String(k || '').trim())
      .filter((k) => /^[0-9a-fA-F]{24}$/.test(k));
    if (valid.length > 0) body.yetkiliKategoriIds = valid;
  }
  if (Array.isArray(yetkiliUrunIcerikIds) && yetkiliUrunIcerikIds.length > 0) {
    const validU = yetkiliUrunIcerikIds
      .map((k) => String(k || '').trim())
      .filter((k) => /^[0-9a-fA-F]{24}$/.test(k));
    if (validU.length > 0) body.yetkiliUrunIcerikIds = validU;
  }
  if (excludeTechServiceId && /^[0-9a-fA-F]{24}$/.test(String(excludeTechServiceId).trim())) {
    body.excludeTechServiceId = String(excludeTechServiceId).trim();
  }
  
  return safeApiRequest(`${SERVICE_AREAS_ENDPOINT}/check-batch`, {
    method: 'POST',
    body,
    skipCsrf: false,
  });
};

/**
 * Yetki alanı ekler
 */
export const createServiceArea = async (payload) => {
  if (!payload.techService || !payload.cityId || !payload.districtId) {
    throw new Error('Teknik servis, il ve ilçe gereklidir');
  }
  
  // Payload'ı dokümantasyona göre formatla
  // Dokümantasyona göre: techService (MongoDB ObjectId string), cityId (1-81 arası tam sayı), districtId (101-8199 arası tam sayı)
  const formattedPayload = {
    techService: String(payload.techService).trim(), // MongoDB ObjectId string olarak (24 karakter hex)
    cityId: Number(payload.cityId), // 1-81 arası tam sayı
    districtId: Number(payload.districtId), // 101-8199 arası tam sayı
  };
  
  // Validasyon: cityId ve districtId'nin geçerli aralıkta olduğunu kontrol et
  if (formattedPayload.cityId < 1 || formattedPayload.cityId > 81) {
    throw new Error('İl ID\'si 1-81 arası olmalıdır');
  }
  
  if (formattedPayload.districtId < 101 || formattedPayload.districtId > 8199) {
    throw new Error('İlçe ID\'si 101-8199 arası olmalıdır');
  }
  
  // MongoDB ObjectId format kontrolü (24 karakter hex string)
  if (!/^[0-9a-fA-F]{24}$/.test(formattedPayload.techService)) {
    throw new Error('Geçersiz teknik servis ID formatı (MongoDB ObjectId olmalıdır)');
  }
  
  if (payload.aciklama && payload.aciklama.trim()) {
    const aciklama = String(payload.aciklama).trim();
    if (aciklama.length > 500) {
      throw new Error('Açıklama maksimum 500 karakter olabilir');
    }
    formattedPayload.aciklama = aciklama;
  }

  if (Array.isArray(payload.yetkiliKategoriIds) && payload.yetkiliKategoriIds.length > 0) {
    const validIds = payload.yetkiliKategoriIds
      .filter((k) => k && /^[0-9a-fA-F]{24}$/.test(String(k).trim()))
      .map((k) => String(k).trim());
    if (validIds.length > 0) {
      formattedPayload.yetkiliKategoriIds = validIds;
    }
  }

  if (Array.isArray(payload.yetkiliUrunIcerikIds) && payload.yetkiliUrunIcerikIds.length > 0) {
    const validU = payload.yetkiliUrunIcerikIds
      .filter((k) => k && /^[0-9a-fA-F]{24}$/.test(String(k).trim()))
      .map((k) => String(k).trim());
    if (validU.length > 0) {
      formattedPayload.yetkiliUrunIcerikIds = validU;
    }
  }

  return safeApiRequest(SERVICE_AREAS_ENDPOINT, {
    method: 'POST',
    body: formattedPayload,
    skipCsrf: false,
  });
};

/**
 * Birden fazla yetki alanı ekler (toplu ekleme)
 * @param {string} techServiceId
 * @param {Array<{cityId: number, districtId: number}>} districts
 * @param {{ yetkiliKategoriIds?: string[], yetkiliUrunIcerikIds?: string[] }} [scope]
 */
export const createMultipleServiceAreas = async (techServiceId, districts, scope = {}) => {
  if (!techServiceId || !Array.isArray(districts) || districts.length === 0) {
    throw new Error('Teknik servis ID ve ilçe listesi gereklidir');
  }

  const extra = {};
  if (scope?.yetkiliKategoriIds?.length) extra.yetkiliKategoriIds = scope.yetkiliKategoriIds;
  if (scope?.yetkiliUrunIcerikIds?.length) extra.yetkiliUrunIcerikIds = scope.yetkiliUrunIcerikIds;

  const promises = districts.map((district) =>
    createServiceArea({
      techService: techServiceId,
      cityId: district.cityId,
      districtId: district.districtId,
      ...extra,
    })
  );

  return Promise.all(promises);
};

/**
 * İlçe ve opsiyonel kategoriye göre yetkili teknik servisleri getirir
 * Endpoint: GET /api/service-areas/authorized-tech-services?districtId=3401&kategoriId=xxx
 * @param {Object} params
 * @param {number} params.districtId - İlçe ID (101-8199)
 * @param {string} [params.kategoriId] - Ürün ana grup kategori ID (opsiyonel)
 */
/**
 * İlçeye göre yetkili servis kaydı (populate’lı tek yetki alanı).
 * Endpoint: GET /api/service-areas/district/:districtId
 * @param {number|string} districtId
 */
/**
 * İlçe yetki özeti (grup bazlı)
 * GET /api/service-areas/district/:districtId/summary
 */
export const fetchDistrictAuthorizationSummary = async (districtId) => {
  if (districtId == null || districtId === '') {
    throw new Error('İlçe ID gereklidir');
  }
  const dId = Number(districtId);
  if (Number.isNaN(dId) || dId < 101 || dId > 8199) {
    throw new Error('Geçersiz ilçe ID (101-8199 arası olmalıdır)');
  }
  return safeApiRequest(`${SERVICE_AREAS_ENDPOINT}/district/${dId}/summary`, {
    method: 'GET',
    skipCsrf: true,
  });
};

export const fetchServiceAreaByDistrict = async (districtId) => {
  if (districtId == null || districtId === '') {
    throw new Error('İlçe ID gereklidir');
  }
  const dId = Number(districtId);
  if (Number.isNaN(dId) || dId < 101 || dId > 8199) {
    throw new Error('Geçersiz ilçe ID (101-8199 arası olmalıdır)');
  }
  return safeApiRequest(`${SERVICE_AREAS_ENDPOINT}/district/${dId}`, {
    method: 'GET',
    skipCsrf: true,
  });
};

/**
 * İle göre aktif yetki alanları (haritada ilçe boyaması için districtId listesi).
 * Endpoint: GET /api/service-areas/city/:cityId — cityId: il plakası 1–81
 * @param {number|string} cityPlate
 */
export const fetchServiceAreasByCityPlate = async (cityPlate) => {
  const c = Number(cityPlate);
  if (!Number.isFinite(c) || c < 1 || c > 81) {
    throw new Error('Geçersiz il plakası (1-81 arası olmalıdır)');
  }
  return safeApiRequest(`${SERVICE_AREAS_ENDPOINT}/city/${c}`, {
    method: 'GET',
    skipCsrf: true,
  });
};

export const fetchAuthorizedTechServices = async ({ districtId, kategoriId, urunIcerikId } = {}) => {
  if (!districtId) {
    throw new Error('İlçe ID gereklidir');
  }
  const dId = Number(districtId);
  if (isNaN(dId) || dId < 101 || dId > 8199) {
    throw new Error('Geçersiz ilçe ID (101-8199 arası olmalıdır)');
  }

  const query = new URLSearchParams();
  query.set('districtId', String(dId));
  if (kategoriId && String(kategoriId).trim() && /^[0-9a-fA-F]{24}$/.test(String(kategoriId).trim())) {
    query.set('kategoriId', String(kategoriId).trim());
  }
  if (urunIcerikId && String(urunIcerikId).trim() && /^[0-9a-fA-F]{24}$/.test(String(urunIcerikId).trim())) {
    query.set('urunIcerikId', String(urunIcerikId).trim());
  }

  return safeApiRequest(`${SERVICE_AREAS_ENDPOINT}/authorized-tech-services?${query.toString()}`, {
    method: 'GET',
    skipCsrf: true,
  });
};

/**
 * Yetki alanını günceller (ör. yetkiliKategoriIds)
 */
export const updateServiceArea = async (serviceAreaId, payload = {}) => {
  if (!serviceAreaId) {
    throw new Error('Yetki alanı ID gereklidir');
  }
  const id = String(serviceAreaId).trim();
  if (!/^[0-9a-fA-F]{24}$/.test(id)) {
    throw new Error('Geçersiz yetki alanı ID formatı');
  }

  const body = {};
  if (payload.aktif !== undefined) body.aktif = !!payload.aktif;
  if (payload.aciklama !== undefined) body.aciklama = String(payload.aciklama).trim();
  if (payload.techService) body.techService = String(payload.techService).trim();

  if (Array.isArray(payload.yetkiliKategoriIds)) {
    body.yetkiliKategoriIds = payload.yetkiliKategoriIds
      .map((k) => String(k || '').trim())
      .filter((k) => /^[0-9a-fA-F]{24}$/.test(k));
  }

  if (Array.isArray(payload.yetkiliUrunIcerikIds)) {
    body.yetkiliUrunIcerikIds = payload.yetkiliUrunIcerikIds
      .map((k) => String(k || '').trim())
      .filter((k) => /^[0-9a-fA-F]{24}$/.test(k));
  }

  return safeApiRequest(`${SERVICE_AREAS_ENDPOINT}/${id}`, {
    method: 'PUT',
    body,
    skipCsrf: false,
  });
};

/**
 * Yetki alanını siler
 */
export const deleteServiceArea = async (serviceAreaId) => {
  if (!serviceAreaId) {
    throw new Error('Yetki alanı ID gereklidir');
  }
  return safeApiRequest(`${SERVICE_AREAS_ENDPOINT}/${serviceAreaId}`, {
    method: 'DELETE',
    skipCsrf: false,
  });
};

/**
 * Yetki alanını başka teknik servise devret
 * Endpoint: POST /api/service-areas/reassign
 */
export const reassignServiceArea = async ({
  targetTechServiceId,
  cityId,
  districtId,
  serviceAreaId,
  yetkiliKategoriIds,
  yetkiliUrunIcerikIds,
} = {}) => {
  if (!targetTechServiceId || cityId == null || districtId == null) {
    throw new Error('Teknik servis, il ve ilçe gereklidir');
  }
  const body = {
    targetTechServiceId: String(targetTechServiceId).trim(),
    cityId: Number(cityId),
    districtId: Number(districtId),
  };
  if (serviceAreaId && /^[0-9a-fA-F]{24}$/.test(String(serviceAreaId).trim())) {
    body.serviceAreaId = String(serviceAreaId).trim();
  }
  if (Array.isArray(yetkiliKategoriIds) && yetkiliKategoriIds.length > 0) {
    body.yetkiliKategoriIds = yetkiliKategoriIds
      .map((k) => String(k || '').trim())
      .filter((k) => /^[0-9a-fA-F]{24}$/.test(k));
  }
  if (Array.isArray(yetkiliUrunIcerikIds) && yetkiliUrunIcerikIds.length > 0) {
    body.yetkiliUrunIcerikIds = yetkiliUrunIcerikIds
      .map((k) => String(k || '').trim())
      .filter((k) => /^[0-9a-fA-F]{24}$/.test(k));
  }
  return safeApiRequest(`${SERVICE_AREAS_ENDPOINT}/reassign`, {
    method: 'POST',
    body,
    skipCsrf: false,
  });
};

